import { FC, memo, useCallback, useMemo } from 'react'
import { RoutePoints } from '@cargomart/ui'
import IconYarche from 'assets/yarche.svg?react'
import cn from 'classnames'
import { ListCard, ListCardRow, ListCardTable, ListRow } from 'components/extend/List'
import some from 'lodash/some'
import { OrderAction } from 'modules/order/components/OrderAction'
import {
  formattedBarcode,
  formattedDepartureTime,
  formattedOrderStatusStyle,
  formattedPaymentStatus,
  formattedSerialId,
  formattedTotalCargoParams
} from 'modules/order/helpers'
import { OrderShortPoint } from 'schema/models/order_short_point'

import { OrderTableColumnProps } from './types'

import s from '../../OrderTable.module.less'

const OrderTableColumn: FC<OrderTableColumnProps> = (props) => {
  const {
    id,
    isTablet,
    isMobile,
    status,
    serialId,
    departureTime,
    departureName,
    destinationName,
    departurePartner,
    departurePointType,
    destinationPartner,
    destinationPointType,
    barcode,
    totalWeight,
    totalVolume,
    price,
    paymentStatus,
    action,
    onCancelButtonClick,
    onClick
  } = props
  const isShowAction = some(action, (current) => Boolean(current))

  const pointTypeView = useCallback((pointType: OrderShortPoint.pointType, name?: string) => {
    if (pointType === OrderShortPoint.pointType.PVZ_YARCHE) {
      return (
        <div className="flex items-center">
          <IconYarche /> {name}
        </div>
      )
    }

    return name
  }, [])

  const mobileHeader = useMemo(
    () => (
      <div className={s.mobileHeader}>
        <div className="flex flex-col">
          <span>
            {formattedSerialId(serialId)} / Трек-№: {barcode}
          </span>
          <span>{formattedTotalCargoParams(totalWeight, totalVolume)}</span>
        </div>
        {isShowAction && (
          <OrderAction access={action} iconClassName={s.actions} id={id} onCancel={onCancelButtonClick} />
        )}
      </div>
    ),
    [action, barcode, id, isShowAction, onCancelButtonClick, serialId, totalVolume, totalWeight]
  )
  const mobileView = useMemo(
    () => (
      <ListCard className={s.mobileName} header={mobileHeader} onClick={onClick} testId="order--order--table--column">
        <ListCardTable className={s.mobileCardTable}>
          <ListCardRow className={cn(s.mobileCardRow, s.pb4)}>
            <div className={cn(s.mobileContent, s.pl0)}>
              <RoutePoints
                size="lg"
                type="vertical"
                endPoint={{
                  title: pointTypeView(departurePointType, destinationName),
                  additionalSubtitle: <div className="text-body-sm">{destinationPartner}</div>
                }}
                startPoint={{
                  title: pointTypeView(departurePointType, departureName),
                  additionalSubtitle: <div className="text-body-sm">{departurePartner}</div>,
                  subtitle: <div className="text-body-sm text-primary">{departureTime},</div>
                }}
              />
            </div>
          </ListCardRow>
          <ListCardRow className={s.mobileCardRow}>
            <div className={cn(s.mobileContent, s.mobileFooter, paymentStatus === 'Без оплаты' ? s.noPaddingTop : '')}>
              <span className={cn(s.status, formattedOrderStatusStyle(status, s, isMobile))}>{status}</span>
              <div>
                <div className={s.price}>{price}</div>
                <div className={cn(s.paymentStatus, formattedPaymentStatus(paymentStatus, s))}>{paymentStatus}</div>
              </div>
            </div>
          </ListCardRow>
        </ListCardTable>
      </ListCard>
    ),
    [
      departureName,
      departurePartner,
      departurePointType,
      departureTime,
      destinationName,
      destinationPartner,
      isMobile,
      mobileHeader,
      onClick,
      paymentStatus,
      pointTypeView,
      price,
      status
    ]
  )
  const tabletHeader = useMemo(
    () => (
      <div className={cn(s.mobileHeader, s.tabletHeader)}>
        <span>{formattedSerialId(serialId)}</span>
        {isShowAction && (
          <OrderAction access={action} iconClassName={s.actions} id={id} onCancel={onCancelButtonClick} />
        )}
      </div>
    ),
    [action, id, isShowAction, onCancelButtonClick, serialId]
  )
  const tabletView = useMemo(
    () => (
      <ListCard className={s.tabletName} header={tabletHeader} onClick={onClick} testId="order--order--table--column">
        <ListCardTable className={s.mobileCardTable}>
          <ListCardRow className={s.mobileCardRow}>
            <div className={s.mobileTitleRow}>Статус</div>
            <div className={s.mobileContent}>{status || '—'}</div>
          </ListCardRow>
          <ListCardRow className={s.mobileCardRow}>
            <div className={s.mobileTitleRow}>Дата погрузки</div>
            <div className={s.mobileContent}>{departureTime || '—'}</div>
          </ListCardRow>
          <ListCardRow className={s.mobileCardRow}>
            <div className={s.mobileTitleRow}>Направление</div>
            <div className={s.mobileContent}>
              <RoutePoints
                endPoint={{
                  title: pointTypeView(destinationPointType, destinationName),
                  additionalSubtitle: destinationPartner
                }}
                startPoint={{
                  title: pointTypeView(departurePointType, departureName),
                  additionalSubtitle: departurePartner
                }}
              />
            </div>
          </ListCardRow>
          <ListCardRow className={s.mobileCardRow}>
            <div className={s.mobileTitleRow}>Трек-номер</div>
            <div className={s.mobileContent}>{barcode || '—'}</div>
          </ListCardRow>
          <ListCardRow className={s.mobileCardRow}>
            <div className={s.mobileTitleRow}>Груз</div>
            <div className={s.mobileContent}>{formattedTotalCargoParams(totalWeight, totalVolume)}</div>
          </ListCardRow>
          <ListCardRow className={s.mobileCardRow}>
            <div className={s.mobileTitleRow}>Цена</div>
            <div className={s.mobileContent}>{price}</div>
          </ListCardRow>
          <ListCardRow className={s.mobileCardRow}>
            {paymentStatus !== 'Без оплаты' ? '' : <div className={s.mobileTitleRow}>Статус оплаты</div>}
            <div className={cn(s.mobileContent, s.paymentStatus, formattedPaymentStatus(paymentStatus, s))}>
              {paymentStatus}
            </div>
          </ListCardRow>
        </ListCardTable>
      </ListCard>
    ),
    [
      barcode,
      departureName,
      departurePartner,
      departurePointType,
      departureTime,
      destinationName,
      destinationPartner,
      destinationPointType,
      onClick,
      paymentStatus,
      pointTypeView,
      price,
      status,
      tabletHeader,
      totalVolume,
      totalWeight
    ]
  )

  const desktopView = useMemo(
    () => (
      <ListRow className={cn(s.item)} onClick={onClick} testId="order--order--table--column">
        <div className={cn(s.column__status)}>
          <div className={cn(s.status, formattedOrderStatusStyle(status, s))}>{status || '—'}</div>
          <div className={s.serialId}>{formattedSerialId(serialId)}</div>
        </div>
        <div className={cn(s.column__departureTime)}>
          <div className={s.departureTime}>{formattedDepartureTime(departureTime)}</div>
        </div>
        <div className={cn(s.column__transportation)}>
          <RoutePoints
            endPoint={{
              title: pointTypeView(destinationPointType, destinationName),
              additionalSubtitle: destinationPartner
            }}
            startPoint={{
              title: pointTypeView(departurePointType, departureName),
              additionalSubtitle: departurePartner
            }}
          />
        </div>
        <div className={s.column__barcode_cargo}>
          <div className={s.barCode}>{formattedBarcode(barcode)}</div>
          <div className={s.cargo}>{formattedTotalCargoParams(totalWeight, totalVolume)}</div>
        </div>
        <div className={s.column__price}>
          <div className={s.price}>{price}</div>
          <div className={cn(s.paymentStatus, formattedPaymentStatus(paymentStatus, s))}>{paymentStatus}</div>
        </div>
        {isShowAction && (
          <OrderAction access={action} iconClassName={s.actions} id={id} onCancel={onCancelButtonClick} />
        )}
      </ListRow>
    ),
    [
      action,
      barcode,
      departureName,
      departurePartner,
      departurePointType,
      departureTime,
      destinationName,
      destinationPartner,
      destinationPointType,
      id,
      isShowAction,
      onCancelButtonClick,
      onClick,
      paymentStatus,
      pointTypeView,
      price,
      serialId,
      status,
      totalVolume,
      totalWeight
    ]
  )

  return useMemo(() => {
    if (isMobile) {
      return mobileView
    }
    if (isTablet) {
      return tabletView
    }

    return desktopView
  }, [desktopView, isMobile, isTablet, mobileView, tabletView])
}

export default memo(OrderTableColumn)
